/**
 * Индикаторы второй волны: то, без чего у трейдера, пришедшего с
 * TradingView, «чего-то не хватает».
 *
 * Облако Ишимоку, каналы Кельтнера и Дончиана, денежный поток (MFI),
 * Williams %R, параболик, Aroon, пивот-уровни и две трёхсвечные фигуры.
 * Правила те же, что у остальных расчётов: `null` — данных ещё не
 * хватило, и ни одно значение не заглядывает в будущее. Где в учебнике
 * линию рисуют «вперёд» (облако Ишимоку), здесь на баре стоит то
 * значение облака, которое было известно на этом баре.
 */
import { atr, ema } from './math.js';
function filled(length) {
    return new Array(length).fill(null);
}
/** Наибольший максимум и наименьший минимум окна `[i - period + 1 - skip, i - skip]`. */
function extremes(bars, period, skip = 0) {
    const high = filled(bars.length);
    const low = filled(bars.length);
    const size = Math.max(1, Math.floor(period));
    for (let i = size - 1 + skip; i < bars.length; i += 1) {
        let hi = -Infinity;
        let lo = Infinity;
        for (let j = i - skip - size + 1; j <= i - skip; j += 1) {
            const bar = bars[j];
            if (bar.high > hi)
                hi = bar.high;
            if (bar.low < lo)
                lo = bar.low;
        }
        high[i] = hi;
        low[i] = lo;
    }
    return { high, low };
}
function midpoint(bars, period) {
    const { high, low } = extremes(bars, period);
    return high.map((value, i) => (value === null || low[i] === null ? null : (value + low[i]) / 2));
}
function shift(values, by) {
    return values.map((_, i) => (i - by >= 0 ? values[i - by] ?? null : null));
}
/**
 * Облако Ишимоку.
 *
 * Линии облака строятся «на `kijun` баров вперёд»; значит, облако над
 * сегодняшним баром посчитано `kijun` баров назад. Так оно здесь и
 * хранится — без чикоу, которая смотрит в прошлое из будущего.
 */
export function ichimoku(bars, tenkanPeriod, kijunPeriod, senkouPeriod) {
    const tenkan = midpoint(bars, tenkanPeriod);
    const kijun = midpoint(bars, kijunPeriod);
    const rawA = tenkan.map((value, i) => (value === null || kijun[i] === null ? null : (value + kijun[i]) / 2));
    const rawB = midpoint(bars, senkouPeriod);
    const spanA = shift(rawA, kijunPeriod);
    const spanB = shift(rawB, kijunPeriod);
    const cloudTop = spanA.map((a, i) => (a === null || spanB[i] === null ? null : Math.max(a, spanB[i])));
    const cloudBottom = spanA.map((a, i) => (a === null || spanB[i] === null ? null : Math.min(a, spanB[i])));
    return { tenkan, kijun, spanA, spanB, cloudTop, cloudBottom };
}
/** Канал Кельтнера: EMA закрытий ± множитель × ATR. */
export function keltner(bars, period, multiplier, atrPeriod) {
    const middle = ema(bars.map((bar) => bar.close), period);
    const range = atr(bars.map((bar) => bar.high), bars.map((bar) => bar.low), bars.map((bar) => bar.close), atrPeriod);
    const upper = middle.map((value, i) => (value === null || range[i] == null ? null : value + multiplier * range[i]));
    const lower = middle.map((value, i) => (value === null || range[i] == null ? null : value - multiplier * range[i]));
    return { upper, middle, lower };
}
/**
 * Канал Дончиана по прошлым `period` свечам, без текущей.
 *
 * С текущей свечой закрытие никогда не выйдет за канал — пробой, ради
 * которого канал и смотрят («черепахи»), было бы не увидеть.
 */
export function donchian(bars, period) {
    const { high, low } = extremes(bars, period, 1);
    const middle = high.map((value, i) => (value === null || low[i] === null ? null : (value + low[i]) / 2));
    return { upper: high, middle, lower: low };
}
/* ------------------------------------------------------------- осцилляторы */
/** Индекс денежного потока: RSI, взвешенный объёмом. 0–100. */
export function mfi(bars, period) {
    const out = filled(bars.length);
    const typical = bars.map((bar) => (bar.high + bar.low + bar.close) / 3);
    const size = Math.max(1, Math.floor(period));
    for (let i = size; i < bars.length; i += 1) {
        let up = 0;
        let down = 0;
        for (let j = i - size + 1; j <= i; j += 1) {
            const flow = typical[j] * bars[j].volume;
            if (typical[j] > typical[j - 1])
                up += flow;
            else if (typical[j] < typical[j - 1])
                down += flow;
        }
        out[i] = up + down === 0 ? 50 : down === 0 ? 100 : 100 - 100 / (1 + up / down);
    }
    return out;
}
/** Williams %R: где закрытие в диапазоне периода, от −100 (у дна) до 0 (у вершины). */
export function williamsR(bars, period) {
    const { high, low } = extremes(bars, period);
    return bars.map((bar, i) => {
        const hi = high[i];
        const lo = low[i];
        if (hi == null || lo == null)
            return null;
        // «Минус ноль» у самой вершины — тот же ноль; без нормализации он сравнивался бы как отдельное число.
        return hi === lo ? -50 : (-100 * (hi - bar.close)) / (hi - lo) + 0;
    });
}
/** Aroon: как давно был максимум и минимум периода. 100 — только что, 0 — давно. */
export function aroon(bars, period) {
    const size = Math.max(1, Math.floor(period));
    const up = filled(bars.length);
    const down = filled(bars.length);
    for (let i = size; i < bars.length; i += 1) {
        let hiAt = i;
        let loAt = i;
        for (let j = i - size; j <= i; j += 1) {
            if (bars[j].high >= bars[hiAt].high)
                hiAt = j;
            if (bars[j].low <= bars[loAt].low)
                loAt = j;
        }
        up[i] = (100 * (size - (i - hiAt))) / size;
        down[i] = (100 * (size - (i - loAt))) / size;
    }
    const oscillator = up.map((value, i) => (value === null || down[i] === null ? null : value - down[i]));
    return { up, down, oscillator };
}
/* ------------------------------------------------------------- параболик */
/** Parabolic SAR: точка-стоп, которая подтягивается за трендом и переворачивается при касании. */
export function parabolicSar(bars, step, maxStep) {
    const out = filled(bars.length);
    if (bars.length < 3)
        return out;
    let up = bars[1].close >= bars[0].close;
    let sar = up ? Math.min(bars[0].low, bars[1].low) : Math.max(bars[0].high, bars[1].high);
    let extreme = up ? Math.max(bars[0].high, bars[1].high) : Math.min(bars[0].low, bars[1].low);
    let factor = step;
    for (let i = 2; i < bars.length; i += 1) {
        const bar = bars[i];
        let next = sar + factor * (extreme - sar);
        if (up) {
            next = Math.min(next, bars[i - 1].low, bars[i - 2].low);
            if (bar.low < next) {
                up = false;
                next = extreme;
                extreme = bar.low;
                factor = step;
            }
            else if (bar.high > extreme) {
                extreme = bar.high;
                factor = Math.min(maxStep, factor + step);
            }
        }
        else {
            next = Math.max(next, bars[i - 1].high, bars[i - 2].high);
            if (bar.high > next) {
                up = true;
                next = extreme;
                extreme = bar.high;
                factor = step;
            }
            else if (bar.low < extreme) {
                extreme = bar.low;
                factor = Math.min(maxStep, factor + step);
            }
        }
        sar = next;
        out[i] = sar;
    }
    return out;
}
const DAY = 86_400_000;
/**
 * Классические пивот-уровни по прошлому периоду.
 *
 * Период выбирается по свечам: внутри дня — прошлые сутки (UTC), на
 * дневных — прошлая неделя, на недельных — прошлый месяц. Уровни
 * текущего периода считаются только по закрытому прошлому.
 */
export function pivots(bars) {
    const pp = filled(bars.length);
    const r1 = filled(bars.length);
    const s1 = filled(bars.length);
    const r2 = filled(bars.length);
    const s2 = filled(bars.length);
    if (bars.length < 2)
        return { pp, r1, s1, r2, s2 };
    const gaps = bars.slice(1, 50).map((bar, i) => bar.time - bars[i].time).sort((a, b) => a - b);
    const spacing = gaps[Math.floor(gaps.length / 2)] ?? DAY;
    const keyOf = (time) => {
        if (spacing < DAY)
            return Math.floor(time / DAY);
        // 1 января 1970 — четверг; +3 дня переносят границу недели на понедельник.
        if (spacing < 7 * DAY)
            return Math.floor((time / DAY + 3) / 7);
        const date = new Date(time);
        return date.getUTCFullYear() * 12 + date.getUTCMonth();
    };
    let key = keyOf(bars[0].time);
    let hi = bars[0].high;
    let lo = bars[0].low;
    let last = bars[0].close;
    let levels = null;
    for (let i = 0; i < bars.length; i += 1) {
        const bar = bars[i];
        const own = keyOf(bar.time);
        if (own !== key) {
            const p = (hi + lo + last) / 3;
            levels = [p, 2 * p - lo, 2 * p - hi, p + (hi - lo), p - (hi - lo)];
            key = own;
            hi = bar.high;
            lo = bar.low;
        }
        else if (i > 0) {
            hi = Math.max(hi, bar.high);
            lo = Math.min(lo, bar.low);
        }
        last = bar.close;
        if (levels) {
            pp[i] = levels[0];
            r1[i] = levels[1];
            s1[i] = levels[2];
            r2[i] = levels[3];
            s2[i] = levels[4];
        }
    }
    return { pp, r1, s1, r2, s2 };
}
/* ------------------------------------------------------------- трёхсвечные фигуры */
function body(bar) {
    return Math.abs(bar.close - bar.open);
}
/**
 * Утренняя и вечерняя звезда.
 *
 * Сильная свеча, затем маленькая (тело не больше трети первого), затем
 * сильная обратная, закрывшаяся за серединой тела первой.
 */
export function stars(bars) {
    const up = new Array(bars.length).fill(0);
    const down = new Array(bars.length).fill(0);
    for (let i = 2; i < bars.length; i += 1) {
        const a = bars[i - 2];
        const b = bars[i - 1];
        const c = bars[i];
        const first = body(a);
        if (first <= 0 || first < (a.high - a.low) * 0.5 || body(b) > first / 3)
            continue;
        const middle = (a.open + a.close) / 2;
        if (a.close < a.open && c.close > c.open && c.close > middle)
            up[i] = 1;
        if (a.close > a.open && c.close < c.open && c.close < middle)
            down[i] = 1;
    }
    return { up, down };
}
/**
 * Три солдата и три вороны: три сильные свечи подряд в одну сторону,
 * каждая открылась внутри тела предыдущей и закрылась дальше неё.
 */
export function threeSoldiers(bars) {
    const up = new Array(bars.length).fill(0);
    const down = new Array(bars.length).fill(0);
    const strong = (bar) => bar.high > bar.low && body(bar) >= (bar.high - bar.low) * 0.5;
    for (let i = 2; i < bars.length; i += 1) {
        const trio = [bars[i - 2], bars[i - 1], bars[i]];
        if (!trio.every(strong))
            continue;
        const rising = trio.every((bar) => bar.close > bar.open) && trio.slice(1).every((bar, n) => bar.close > trio[n].close && bar.open >= trio[n].open && bar.open <= trio[n].close);
        const falling = trio.every((bar) => bar.close < bar.open) && trio.slice(1).every((bar, n) => bar.close < trio[n].close && bar.open <= trio[n].open && bar.open >= trio[n].close);
        if (rising)
            up[i] = 1;
        if (falling)
            down[i] = 1;
    }
    return { up, down };
}
//# sourceMappingURL=ta-extra.js.map